import { getApps, initializeApp } from 'firebase/app';
import { getAuth, onAuthStateChanged, GoogleAuthProvider, signInWithCredential, signInWithPopup, OAuthProvider, FacebookAuthProvider, signInWithEmailAndPassword, createUserWithEmailAndPassword, updateProfile, signOut, sendPasswordResetEmail } from 'firebase/auth';
import { createContext, useState, useEffect, useCallback, useContext } from 'react';
import { Capacitor } from '@capacitor/core';
import { jsx } from 'react/jsx-runtime';

// src/config.ts
var firebaseApp = null;
var firebaseAuth = null;
var firebaseConfig = null;
function initializeFirebase(config) {
  firebaseConfig = config;
  if (getApps().length === 0) {
    firebaseApp = initializeApp(config);
  } else {
    firebaseApp = getApps()[0];
  }
  return firebaseApp;
}
function getFirebaseConfig() {
  if (!firebaseConfig) {
    throw new Error("Firebase has not been initialized. Call initializeFirebase() first.");
  }
  return firebaseConfig;
}
function getFirebaseApp() {
  if (!firebaseApp) {
    throw new Error("Firebase has not been initialized. Call initializeFirebase() first.");
  }
  return firebaseApp;
}
function getFirebaseAuth() {
  if (!firebaseAuth) {
    firebaseAuth = getAuth(getFirebaseApp());
  }
  return firebaseAuth;
}
var DEBUG = true;
var isNative = Capacitor.isNativePlatform();
var IOS_CLIENT_ID = "3111614095-gijqq833loc59t7qqcscnbsq5h6le0dv.apps.googleusercontent.com";
var WEB_CLIENT_ID = "3111614095-f8p5r23tl3arscdgilqc7bo1bomq60sh.apps.googleusercontent.com";
var SocialLogin = null;
var socialLoginInitialized = false;
var socialLoginPromise = null;
if (isNative) {
  console.log("[Auth] Starting TOP-LEVEL SocialLogin import...");
  socialLoginPromise = import('@capgo/capacitor-social-login').then((module) => {
    SocialLogin = module.SocialLogin;
    console.log("[Auth] TOP-LEVEL SocialLogin module loaded, initializing...");
    return SocialLogin.initialize({
      google: {
        iOSClientId: IOS_CLIENT_ID,
        iOSServerClientId: WEB_CLIENT_ID
      }
    });
  }).then(() => {
    socialLoginInitialized = true;
    console.log("[Auth] SocialLogin initialized successfully");
  }).catch((err) => {
    console.error("[Auth] Failed to load/initialize SocialLogin:", err);
  });
}
var AuthContext = createContext(null);
function firebaseUserToPaneonUser(firebaseUser) {
  return {
    id: firebaseUser.uid,
    email: firebaseUser.email || "",
    name: firebaseUser.displayName || void 0,
    avatarUrl: firebaseUser.photoURL || void 0,
    createdAt: firebaseUser.metadata.creationTime ? new Date(firebaseUser.metadata.creationTime).getTime() / 1e3 : Date.now() / 1e3,
    updatedAt: Date.now() / 1e3
  };
}
function AuthProvider({ children, onAuthStateChange }) {
  const [state, setState] = useState({
    user: null,
    isAuthenticated: false,
    isLoading: true,
    error: null
  });
  useEffect(() => {
    console.log("[Auth] isNative:", isNative);
    if (isNative) console.log("[Auth] SocialLogin will be loaded via top-level import");
  }, []);
  useEffect(() => {
    console.log("[Auth] Setting up Firebase auth listener...");
    const auth = getFirebaseAuth();
    const timeout = setTimeout(() => {
      console.log("[Auth] Timeout reached, setting loading to false");
      setState((prev) => ({ ...prev, isLoading: false }));
    }, 5e3);
    const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
      console.log("[Auth] onAuthStateChanged fired, user:", firebaseUser?.email || "null");
      clearTimeout(timeout);
      if (firebaseUser) {
        const user = firebaseUserToPaneonUser(firebaseUser);
        setState({
          user,
          isAuthenticated: true,
          isLoading: false,
          error: null
        });
        onAuthStateChange?.(user);
      } else {
        setState({
          user: null,
          isAuthenticated: false,
          isLoading: false,
          error: null
        });
        onAuthStateChange?.(null);
      }
    });
    return () => {
      clearTimeout(timeout);
      unsubscribe();
    };
  }, [onAuthStateChange]);
  const signInWithGoogle = useCallback(async () => {
    try {
      setState((prev) => ({ ...prev, isLoading: true, error: null }));
      const auth = getFirebaseAuth();
      if (isNative) {
        if (!socialLoginInitialized && socialLoginPromise) {
          if (DEBUG) console.log("[Auth] Waiting for SocialLogin to initialize...");
          await socialLoginPromise;
        }
        if (!SocialLogin || !socialLoginInitialized) {
          throw new Error("SocialLogin module not available on native platform");
        }
        if (DEBUG) console.log("[Auth] Starting native Google Sign-In...");
        if (DEBUG) console.log("[Auth] SocialLogin initialized:", socialLoginInitialized);
        const result = await SocialLogin.login({
          provider: "google",
          options: {
            scopes: ["email", "profile", "openid"]
          }
        });
        if (DEBUG) console.log("[Auth] SocialLogin.login result:", JSON.stringify(result, null, 2));
        if (result.provider === "google" && result.result) {
          const googleResult = result.result;
          if (DEBUG) console.log("[Auth] Google result - idToken exists:", !!googleResult.idToken);
          if (DEBUG)
            console.log("[Auth] Google result - accessToken exists:", !!googleResult.accessToken);
          if (googleResult.idToken) {
            try {
              if (DEBUG) console.log("[Auth] Using Firebase REST API for native sign-in...");
              if (DEBUG) console.log("[Auth] idToken length:", googleResult.idToken.length);
              const config = getFirebaseConfig();
              const response = await fetch(
                `https://identitytoolkit.googleapis.com/v1/accounts:signInWithIdp?key=${config.apiKey}`,
                {
                  method: "POST",
                  headers: { "Content-Type": "application/json" },
                  body: JSON.stringify({
                    postBody: `id_token=${googleResult.idToken}&providerId=google.com`,
                    requestUri: "http://localhost",
                    returnSecureToken: true,
                    returnIdpCredential: true
                  })
                }
              );
              if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error?.message || "Firebase REST API failed");
              }
              const data = await response.json();
              if (DEBUG) console.log("[Auth] Firebase REST API success, user:", data.email);
              if (DEBUG) console.log("[Auth] Signing in with returned credential...");
              const credential = GoogleAuthProvider.credential(data.oauthIdToken);
              try {
                const credentialPromise = signInWithCredential(auth, credential);
                const timeoutPromise = new Promise(
                  (_, reject) => setTimeout(() => reject(new Error("SDK timeout")), 5e3)
                );
                await Promise.race([credentialPromise, timeoutPromise]);
                if (DEBUG) console.log("[Auth] SDK signInWithCredential succeeded");
              } catch (sdkError) {
                if (DEBUG) console.log("[Auth] SDK failed, using REST API response directly");
              }
              const user = {
                id: data.localId,
                email: data.email || "",
                name: data.displayName || void 0,
                avatarUrl: data.photoUrl || void 0,
                createdAt: Date.now() / 1e3,
                updatedAt: Date.now() / 1e3
              };
              setState({
                user,
                isAuthenticated: true,
                isLoading: false,
                error: null
              });
              onAuthStateChange?.(user);
              return;
            } catch (firebaseError) {
              console.error("[Auth] Firebase sign-in failed:", firebaseError);
              throw firebaseError;
            }
          } else {
            console.error("[Auth] No ID token in result!");
            throw new Error("No ID token received from Google Sign-In");
          }
        } else {
          console.error("[Auth] Invalid result structure:", result);
          throw new Error("Invalid response from Google Sign-In");
        }
      } else {
        if (DEBUG) console.log("[Auth] Using web popup sign-in");
        const provider = new GoogleAuthProvider();
        await signInWithPopup(auth, provider);
      }
    } catch (error) {
      console.error("[Auth] Google sign-in error:", error);
      setState((prev) => ({
        ...prev,
        isLoading: false,
        error: error instanceof Error ? error.message : "Google sign-in failed"
      }));
      throw error;
    }
  }, [onAuthStateChange]);
  const signInWithApple = useCallback(async () => {
    try {
      setState((prev) => ({ ...prev, isLoading: true, error: null }));
      const auth = getFirebaseAuth();
      if (isNative && SocialLogin) {
        if (DEBUG) console.log("[Auth] Starting native Apple Sign-In...");
        const result = await SocialLogin.login({
          provider: "apple",
          options: {
            scopes: ["email", "name"]
          }
        });
        if (DEBUG) console.log("[Auth] Apple SocialLogin result:", JSON.stringify(result, null, 2));
        if (result.provider === "apple" && result.result) {
          const appleResult = result.result;
          if (appleResult.idToken) {
            try {
              if (DEBUG) console.log("[Auth] Using Firebase REST API for native Apple sign-in...");
              if (DEBUG) console.log("[Auth] idToken length:", appleResult.idToken.length);
              const config = getFirebaseConfig();
              const response = await fetch(
                `https://identitytoolkit.googleapis.com/v1/accounts:signInWithIdp?key=${config.apiKey}`,
                {
                  method: "POST",
                  headers: { "Content-Type": "application/json" },
                  body: JSON.stringify({
                    postBody: `id_token=${appleResult.idToken}&providerId=apple.com`,
                    requestUri: "http://localhost",
                    returnSecureToken: true,
                    returnIdpCredential: true
                  })
                }
              );
              if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error?.message || "Firebase REST API failed");
              }
              const data = await response.json();
              if (DEBUG) console.log("[Auth] Firebase REST API success, user:", data.email);
              try {
                const provider = new OAuthProvider("apple.com");
                const credential = provider.credential({
                  idToken: appleResult.idToken,
                  rawNonce: appleResult.nonce
                });
                const credentialPromise = signInWithCredential(auth, credential);
                const timeoutPromise = new Promise(
                  (_, reject) => setTimeout(() => reject(new Error("SDK timeout")), 5e3)
                );
                await Promise.race([credentialPromise, timeoutPromise]);
                if (DEBUG) console.log("[Auth] SDK signInWithCredential succeeded");
              } catch (sdkError) {
                if (DEBUG) console.log("[Auth] SDK failed, using REST API response directly");
              }
              const user = {
                id: data.localId,
                email: data.email || appleResult.email || "",
                name: data.displayName || (appleResult.givenName && appleResult.familyName ? `${appleResult.givenName} ${appleResult.familyName}` : void 0),
                avatarUrl: data.photoUrl || void 0,
                createdAt: Date.now() / 1e3,
                updatedAt: Date.now() / 1e3
              };
              setState({
                user,
                isAuthenticated: true,
                isLoading: false,
                error: null
              });
              onAuthStateChange?.(user);
              return;
            } catch (firebaseError) {
              console.error("[Auth] Firebase Apple sign-in failed:", firebaseError);
              throw firebaseError;
            }
          } else {
            throw new Error("No ID token received from Apple Sign-In");
          }
        }
      } else {
        const provider = new OAuthProvider("apple.com");
        provider.addScope("email");
        provider.addScope("name");
        await signInWithPopup(auth, provider);
      }
    } catch (error) {
      console.error("[Auth] Apple sign-in error:", error);
      setState((prev) => ({
        ...prev,
        isLoading: false,
        error: error instanceof Error ? error.message : "Apple sign-in failed"
      }));
      throw error;
    }
  }, [SocialLogin, onAuthStateChange]);
  const signInWithFacebook = useCallback(async () => {
    try {
      setState((prev) => ({ ...prev, isLoading: true, error: null }));
      const auth = getFirebaseAuth();
      const provider = new FacebookAuthProvider();
      await signInWithPopup(auth, provider);
    } catch (error) {
      setState((prev) => ({
        ...prev,
        isLoading: false,
        error: error instanceof Error ? error.message : "Facebook sign-in failed"
      }));
      throw error;
    }
  }, []);
  const signInWithEmail = useCallback(async (email, password) => {
    try {
      setState((prev) => ({ ...prev, isLoading: true, error: null }));
      const auth = getFirebaseAuth();
      await signInWithEmailAndPassword(auth, email, password);
    } catch (error) {
      setState((prev) => ({
        ...prev,
        isLoading: false,
        error: error instanceof Error ? error.message : "Email sign-in failed"
      }));
      throw error;
    }
  }, []);
  const signUpWithEmail = useCallback(async (email, password, name) => {
    try {
      setState((prev) => ({ ...prev, isLoading: true, error: null }));
      const auth = getFirebaseAuth();
      const result = await createUserWithEmailAndPassword(auth, email, password);
      if (name && result.user) {
        await updateProfile(result.user, { displayName: name });
      }
    } catch (error) {
      setState((prev) => ({
        ...prev,
        isLoading: false,
        error: error instanceof Error ? error.message : "Sign-up failed"
      }));
      throw error;
    }
  }, []);
  const signOut$1 = useCallback(async () => {
    try {
      if (DEBUG) console.log("[Auth] Signing out... isNative:", isNative);
      if (isNative && SocialLogin) {
        if (DEBUG) console.log("[Auth] Signing out from native social providers...");
        await Promise.all([
          SocialLogin.logout({ provider: "google" }).catch((err) => {
            console.warn("[Auth] Native Google logout error (ignored):", err);
          }),
          SocialLogin.logout({ provider: "apple" }).catch((err) => {
            console.warn("[Auth] Native Apple logout error (ignored):", err);
          })
        ]);
      }
      const auth = getFirebaseAuth();
      if (DEBUG) console.log("[Auth] Signing out from Firebase...");
      if (isNative) {
        const signOutPromise = signOut(auth);
        const timeoutPromise = new Promise((resolve) => {
          setTimeout(() => {
            if (DEBUG) console.log("[Auth] Firebase signOut timeout, clearing local state anyway");
            resolve();
          }, 3e3);
        });
        await Promise.race([signOutPromise, timeoutPromise]);
      } else {
        await signOut(auth);
      }
      setState({
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: null
      });
      if (DEBUG) console.log("[Auth] Sign out complete");
    } catch (error) {
      console.error("[Auth] Sign out error:", error);
      setState({
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: error instanceof Error ? error.message : "Sign-out failed"
      });
    }
  }, [SocialLogin]);
  const resetPassword = useCallback(async (email) => {
    try {
      const auth = getFirebaseAuth();
      await sendPasswordResetEmail(auth, email);
    } catch (error) {
      throw error;
    }
  }, []);
  const updateUserProfile = useCallback(
    async (data) => {
      try {
        const auth = getFirebaseAuth();
        if (auth.currentUser) {
          await updateProfile(auth.currentUser, data);
          setState((prev) => ({
            ...prev,
            user: prev.user ? {
              ...prev.user,
              name: data.displayName ?? prev.user.name,
              avatarUrl: data.photoURL ?? prev.user.avatarUrl
            } : null
          }));
        }
      } catch (error) {
        throw error;
      }
    },
    []
  );
  const value = {
    ...state,
    signInWithGoogle,
    signInWithApple,
    signInWithFacebook,
    signInWithEmail,
    signUpWithEmail,
    signOut: signOut$1,
    resetPassword,
    updateUserProfile
  };
  return /* @__PURE__ */ jsx(AuthContext.Provider, { value, children });
}
function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
function useRequireAuth() {
  const auth = useAuth();
  if (!auth.user) {
    throw new Error("User must be authenticated");
  }
  return auth;
}

export { AuthProvider, getFirebaseApp, getFirebaseAuth, initializeFirebase, useAuth, useRequireAuth };
//# sourceMappingURL=index.mjs.map
//# sourceMappingURL=index.mjs.map